import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Sync as SyncIcon } from '@material-ui/icons'
import { GetApp as GetAppIcon } from '@material-ui/icons'
import { useStyles } from '../styles'
import {
  handleCoproprietaire,
  handleContact,
  handleDigital,
  handleLRAR,
  handleStatus,
} from '../utils'
import { useNavigate } from 'react-router'
import { useTranslate } from 'react-admin'
import { useState } from 'react'
import Api from '../../../Apis/api.service'
import { downloadBase64File } from '../../../utils/fileHelpers'

const getDepositProofStatus = (status) => {
  const statusMap = {
    STANDBY: {
      label: 'En attente',
      color: '#999',
      backgroundColor: '#f5f5f5',
    },
    PROCESSING: {
      label: 'En cours',
      color: '#fff',
      backgroundColor: '#2196f3',
    },
    SENT: {
      label: 'Envoyé',
      color: '#fff',
      backgroundColor: '#4caf50',
    },
    WAITING_DEPOSIT_PROOF: {
      label: 'Preuve en attente',
      color: '#fff',
      backgroundColor: '#ff9800',
    },
    END: {
      label: 'Terminé',
      color: '#fff',
      backgroundColor: '#4caf50',
    },
    ERROR: {
      label: 'Erreur',
      color: '#fff',
      backgroundColor: '#f44336',
    },
  }

  return (
    statusMap[status] || {
      label: status,
      color: '#333',
      backgroundColor: '#e0e0e0',
    }
  )
}

const ConvocationsContent = ({
  id,
  convocations,
  isSyncing = false,
  isRecipientPreview = false,
  isLoading = false,
}) => {
  const navigate = useNavigate()
  const translate = useTranslate()
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [dataFile, setDataFile] = useState('')
  const [downloading, setDownloading] = useState(null)

  const getPreviewFile = async (file) => {
    console.log({ file })
    setOpen(true)
    setDataFile(file)
  }

  const downloadDepositProof = async (recipientId, convocationId) => {
    setDownloading(convocationId)
    try {
      const response = await Api.athome.getDepositProof(recipientId)

      if (response && response.data && response.data.depositProof) {
        downloadBase64File(
          response.data.depositProof,
          `preuve-depot-recipient-${recipientId}.pdf`,
          'application/pdf'
        )
      } else {
        alert('Preuve de dépôt non disponible pour ce destinataire')
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement de la preuve:', error)
      alert(
        'Impossible de télécharger la preuve de dépôt. Veuillez réessayer plus tard.'
      )
    } finally {
      setDownloading(null)
    }
  }
  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: 4,
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary">
          Chargement des convocations...
        </Typography>
      </Box>
    )

  if (convocations?.length == 0) return null

  return (
    <>
      {isSyncing && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            marginBottom: 2,
          }}
        >
          <CircularProgress size={20} />
          <SyncIcon style={{ animation: 'spin 2s linear infinite' }} />
          <Typography variant="body2" color="primary">
            Synchronisation en cours avec Athome...
          </Typography>
        </Box>
      )}
      <TableContainer className={classes.spacing}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {!isRecipientPreview && <TableCell>Statut</TableCell>}
              <TableCell align="left">Copropriétaire</TableCell>
              <TableCell align="left">Personne convoquée</TableCell>
              {!isRecipientPreview && (
                <TableCell align="center">Convocation</TableCell>
              )}
              {!isRecipientPreview && (
                <TableCell align="center">Preuve de dépôt</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {convocations.map((convocation) => {
              const digital = handleDigital(
                convocation,
                id,
                navigate,
                translate,
                getPreviewFile
              )
              const lrar = handleLRAR(
                convocation,
                id,
                translate,
                getPreviewFile
              )
              return (
                <TableRow
                  key={convocation.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {!isRecipientPreview && (
                    <TableCell component="th" scope="convocation">
                      {handleStatus(convocation.status)}
                    </TableCell>
                  )}
                  <TableCell align="left">
                    {handleCoproprietaire(convocation)}
                  </TableCell>
                  <TableCell align="left">
                    {handleContact(convocation.contact, translate)}
                  </TableCell>
                  {!isRecipientPreview && (
                    <TableCell align="left">{lrar ? lrar : digital}</TableCell>
                  )}
                  {!isRecipientPreview && (
                    <TableCell align="center">
                      {convocation.athome_recipient_id ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          {convocation.has_deposit_proof ? (
                            <Tooltip title="Télécharger la preuve de dépôt">
                              <span>
                                <IconButton
                                  onClick={() =>
                                    downloadDepositProof(
                                      convocation.athome_recipient_id,
                                      convocation.id
                                    )
                                  }
                                  disabled={downloading === convocation.id}
                                  size="small"
                                  color="primary"
                                >
                                  {downloading === convocation.id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <GetAppIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            convocation.athome_recipient_deposit_status && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Chip
                                  label={
                                    getDepositProofStatus(
                                      convocation.athome_recipient_deposit_status
                                    ).label
                                  }
                                  size="small"
                                  style={{
                                    color: getDepositProofStatus(
                                      convocation.athome_recipient_deposit_status
                                    ).color,
                                    backgroundColor: getDepositProofStatus(
                                      convocation.athome_recipient_deposit_status
                                    ).backgroundColor,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                  }}
                                />
                              </Box>
                            )
                          )}
                        </Box>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>
                          Non disponible
                        </span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <PreviewFile
        filePath={dataFile}
        open={open}
        onClose={() => {
          setOpen(false)
        }}
      />
    </>
  )
}
const PreviewFile = ({ filePath, open, onClose }) => {
  return (
    <Dialog
      onClick={(e) => e.stopPropagation()}
      onClose={onClose}
      fullWidth="true"
      maxWidth="md"
      open={open}
    >
      <embed
        src={`${window.location.origin}${filePath}`}
        type="application/pdf"
        width="1000px"
        height="600px"
      />
    </Dialog>
  )
}

export default ConvocationsContent
